import { Button, Skeleton } from '@monksflow/monks-ui';
import { RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AutoAwesomeIcon, VideoTemplateIcon } from '@/shared/ui/Icons';
import { VideoCard } from './components/VideoCard';
import type { VideoSectionProps } from './VideoSection.types';

const VIDEO_GRID = 'grid grid-cols-1 gap-6 md:grid-cols-2';
const PLACEHOLDER_SURFACE = 'rounded-2xl border border-dashed border-border bg-muted';

interface VideoSkeletonProps {
  label: string;
  statusLabel?: string;
}

function VideoSkeleton({ label, statusLabel }: VideoSkeletonProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div
        className={`flex aspect-video flex-col items-center justify-center gap-2 ${PLACEHOLDER_SURFACE}`}
      >
        <Skeleton data-testid="video-skeleton" className="size-10 rounded-full" />
        <p className="text-xs text-muted-foreground">{statusLabel}</p>
      </div>
    </div>
  );
}

export function VideoSection(props: VideoSectionProps) {
  const { t } = useTranslation('audiences');

  const {
    thumbnailVideoAssetId,
    detailVideoAssetId,
    isProcessing,
    processingLabel,
    onRegenerateClick,
    onGenerateClick,
    thumbnailMetadata,
    detailMetadata,
    thumbnailFailed,
    detailFailed,
    thumbnailErrorMessage,
    detailErrorMessage,
    retryingVideoType,
    onRetryVideo,
  } = props;

  const hasVideos = !!(
    thumbnailVideoAssetId ||
    detailVideoAssetId ||
    thumbnailFailed ||
    detailFailed
  );
  const statusLabel = isProcessing
    ? (processingLabel ?? t('audiences.videoSection.generating'))
    : undefined;

  // Loading state: full generation in progress. A single-video retry is excluded —
  // that renders inline on the affected VideoCard instead of replacing the section.
  const isRetrying = retryingVideoType != null;
  if (isProcessing && !isRetrying) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm font-medium">{t('audiences.videoSection.thumbnailDetailVideo')}</p>

        <div className={VIDEO_GRID}>
          <VideoSkeleton
            label={t('audiences.videoSection.thumbnailVideo')}
            statusLabel={statusLabel}
          />
          <VideoSkeleton
            label={t('audiences.videoSection.detailVideo')}
            statusLabel={statusLabel}
          />
        </div>
      </div>
    );
  }

  // Empty state: no videos and not processing
  if (!hasVideos) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm font-medium">{t('audiences.videoSection.thumbnailDetailVideo')}</p>

        <div
          className={`flex flex-col items-center justify-center gap-5 py-10 ${PLACEHOLDER_SURFACE}`}
        >
          <div className="flex size-10 items-center justify-center rounded-full bg-accent">
            <VideoTemplateIcon className="size-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm text-muted-foreground">
              {t('audiences.videoSection.noVideosGeneratedYet')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('audiences.videoSection.generateThumbnailAndDetailVideos')}
            </p>
          </div>
          <Button onClick={() => void onGenerateClick()} disabled={isProcessing}>
            <AutoAwesomeIcon className="size-5" />
            {t('audiences.videoSection.generateVideos')}
          </Button>
        </div>
      </div>
    );
  }

  // Populated state: has videos
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{t('audiences.videoSection.thumbnailDetailVideo')}</p>
          {statusLabel && <p className="text-xs text-muted-foreground">{statusLabel}</p>}
        </div>
        <Button variant="outline" onClick={onRegenerateClick} disabled={isProcessing}>
          <RefreshCwIcon />
          {t('audiences.videoSection.reGenerateVideo')}
        </Button>
      </div>

      <div className={VIDEO_GRID}>
        <VideoCard
          label={t('audiences.videoSection.thumbnailVideo')}
          videoAssetId={thumbnailVideoAssetId}
          metadata={thumbnailMetadata}
          hasError={thumbnailFailed}
          errorMessage={thumbnailErrorMessage}
          isRetrying={retryingVideoType === 'mouseOverVideo'}
          onRetry={onRetryVideo ? () => onRetryVideo('mouseOverVideo') : undefined}
        />
        <VideoCard
          label={t('audiences.videoSection.detailVideo')}
          videoAssetId={detailVideoAssetId}
          metadata={detailMetadata}
          hasError={detailFailed}
          errorMessage={detailErrorMessage}
          isRetrying={retryingVideoType === 'video'}
          onRetry={onRetryVideo ? () => onRetryVideo('video') : undefined}
        />
      </div>
    </div>
  );
}
